import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { SplashScreen } from 'src/components/loading-screen';

import useAuthStore, { AUTH_MODULE_ID, waitForAuthReady } from '../store/firebase/auth-store';

// ----------------------------------------------------------------------

const getLoginPath = (type: string) => {
  switch (type) {
    case 'dashboard':
      return paths.auth.login;
    case 'companion':
      return paths.auth.loginCompanion;
    default:
      return paths.auth.login;
  }
};

// ----------------------------------------------------------------------

type Props = {
  type: 'dashboard' | 'companion';
  children: React.ReactNode;
};

export default function AuthGuard({ children, type = 'dashboard' }: Props) {
  const router = useRouter();
  const { authenticated, authInitialized, loading, user } = useAuthStore();
  const [isReady, setIsReady] = useState(false);

  // Debug logging for auth state - using console.warn for visibility
  console.warn('[AuthGuard] RENDER - MODULE_ID:', AUTH_MODULE_ID);
  console.warn('[AuthGuard] RENDER:', {
    authenticated,
    authInitialized,
    loading,
    isReady,
    hasUser: !!user,
    userId: user?.uid?.substring(0, 8) || 'none',
    path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
  });

  // Wait for Firebase auth to be ready before making any decisions
  useEffect(() => {
    let mounted = true;
    console.warn('[AuthGuard] useEffect - Waiting for auth ready...');

    waitForAuthReady().then(() => {
      console.warn('[AuthGuard] waitForAuthReady resolved, mounted:', mounted);
      if (mounted) {
        console.warn('[AuthGuard] Setting isReady=true');
        setIsReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Handle redirect after auth is ready
  const check = useCallback(() => {
    console.warn('[AuthGuard] check():', { isReady, authInitialized, authenticated });
    if (isReady && authInitialized && !authenticated) {
      // Preserve the full URL including pathname, search params, and hash
      const fullPath = window.location.pathname + window.location.search + window.location.hash;
      const searchParams = new URLSearchParams({
        returnTo: fullPath,
      }).toString();
      const loginPath = getLoginPath(type);
      const href = `${loginPath}?${searchParams}`;
      console.warn('[AuthGuard] NOT AUTHENTICATED - Redirecting to:', href);
      router.replace(href);
    }
  }, [isReady, authInitialized, authenticated, router, type]);

  useEffect(() => {
    check();
  }, [check]);

  // Show splash screen until we know the auth state
  if (!isReady || !authInitialized) {
    console.warn('[AuthGuard] Showing SplashScreen:', { isReady, authInitialized });
    return <SplashScreen />;
  }

  // If not authenticated after auth is ready, show splash while redirecting
  if (!authenticated) {
    console.warn('[AuthGuard] Not authenticated - showing splash while redirecting');
    return <SplashScreen />;
  }

  console.warn('[AuthGuard] AUTHENTICATED - rendering children');
  return <>{children}</>;
}
